import { HealthSex } from './healthCalorieGoalCalc';
import {
    calculateMacroStrategy,
    DEFAULT_MACRO_STRATEGY,
    type MacroStrategyId,
    type NutrientGoalBand,
    type NutrientGoalsInput,
} from './macroStrategies';
import {
    ALL_NUTRIENT_KEYS,
    isNutrientKey,
    normalizeNutrientValues,
    NutrientKey,
    roundNutrientAmount,
    type NutrientValue,
} from './nutrientCore';

export type { NutrientGoalsInput } from './macroStrategies';
export {
    ALL_NUTRIENT_KEYS,
    formatNutrientAmount,
    isNutrientKey,
    normalizeNutrientValues,
    NUTRIENT_AMOUNT_DECIMALS,
    NutrientKey,
    roundNutrientAmount,
    type NutrientValue,
} from './nutrientCore';

export const PRIMARY_NUTRIENT_KEYS: NutrientKey[] = [NutrientKey.PROTEIN, NutrientKey.CARBS, NutrientKey.FAT];

export const NUTRIENT_DEFS: Record<NutrientKey, { label: string; primary: boolean }> = {
    [NutrientKey.PROTEIN]: { label: 'Protein', primary: true },
    [NutrientKey.CARBS]: { label: 'Carbs', primary: true },
    [NutrientKey.FAT]: { label: 'Fat', primary: true },
    [NutrientKey.SATURATED_FAT]: { label: 'Saturated fat', primary: false },
    [NutrientKey.SUGAR]: { label: 'Sugar', primary: false },
    [NutrientKey.FIBER]: { label: 'Fiber', primary: false },
    [NutrientKey.SALT]: { label: 'Salt', primary: false },
};

export function scaleNutrients(values: NutrientValue[], factor: number): NutrientValue[] {
    if (!values.length) return [];
    const safeFactor = factor > 0 ? factor : 1;
    return normalizeNutrientValues(values.map(row => ({ key: row.key, amount: row.amount * safeFactor })));
}

export function applyNutrientPortion(baseNutrients: NutrientValue[], mealScale: number): NutrientValue[] {
    return scaleNutrients(baseNutrients, mealScale > 0 ? mealScale : 1);
}

export function sumNutrients(batches: NutrientValue[][]): NutrientValue[] {
    const totals = new Map<NutrientKey, number>();
    for (const batch of batches) {
        for (const row of normalizeNutrientValues(batch)) {
            totals.set(row.key, (totals.get(row.key) ?? 0) + row.amount);
        }
    }
    return ALL_NUTRIENT_KEYS.filter(key => totals.has(key)).map(key => ({
        key,
        amount: roundNutrientAmount(totals.get(key)!),
    }));
}

export function nutrientAmountForKey(values: NutrientValue[] | null | undefined, key: NutrientKey): number {
    return normalizeNutrientValues(values).find(row => row.key === key)?.amount ?? 0;
}

export function defaultMacroGoalsFromKcalGoal(kcalGoal: number): NutrientValue[] {
    const goal = Math.max(0, Math.round(kcalGoal));
    if (goal <= 0) return [];
    return normalizeNutrientValues([
        { key: NutrientKey.PROTEIN, amount: (goal * 0.3) / 4 },
        { key: NutrientKey.CARBS, amount: (goal * 0.4) / 4 },
        { key: NutrientKey.FAT, amount: (goal * 0.3) / 9 },
    ]);
}

export type NutrientGoalsPlan = {
    strategy: MacroStrategyId;
    goals: NutrientValue[];
    bands: Partial<Record<NutrientKey, NutrientGoalBand>>;
    ceilings: NutrientValue[];
};

export function macroGoalsFromProfile(input: NutrientGoalsInput, strategy: MacroStrategyId = DEFAULT_MACRO_STRATEGY): NutrientValue[] {
    const kcal = Math.max(0, Math.round(input.kcalGoal));
    if (kcal <= 0) return [];
    return calculateMacroStrategy({ ...input, kcalGoal: kcal }, strategy).macros;
}

export const DEFAULT_SALT_GOAL_G = 5;

const FIBER_GOAL_BY_SEX: Record<HealthSex, number> = {
    [HealthSex.MALE]: 38,
    [HealthSex.FEMALE]: 25,
};

export function defaultFiberGoalG(kcalGoal: number, sex?: HealthSex | null): number {
    const fromKcal = Math.round((Math.max(0, kcalGoal) / 1000) * 14);
    if (sex != null) {
        return Math.max(FIBER_GOAL_BY_SEX[sex], fromKcal);
    }
    return Math.max(fromKcal, 25);
}

export function defaultSaltGoalG(): number {
    return DEFAULT_SALT_GOAL_G;
}

export function nutrientGoalsPlanFromProfile(
    input: NutrientGoalsInput,
    strategy: MacroStrategyId = DEFAULT_MACRO_STRATEGY,
): NutrientGoalsPlan | null {
    const goal = Math.max(0, Math.round(input.kcalGoal));
    if (goal <= 0) return null;

    const ctx = { ...input, kcalGoal: goal };
    const { macros, bands, ceilings } = calculateMacroStrategy(ctx, strategy);

    return {
        strategy,
        goals: normalizeNutrientValues([
            ...macros,
            { key: NutrientKey.FIBER, amount: defaultFiberGoalG(goal, input.sex) },
            { key: NutrientKey.SALT, amount: defaultSaltGoalG() },
        ]),
        bands,
        ceilings,
    };
}

export function nutrientGoalsFromProfile(input: NutrientGoalsInput, strategy: MacroStrategyId = DEFAULT_MACRO_STRATEGY): NutrientValue[] {
    return nutrientGoalsPlanFromProfile(input, strategy)?.goals ?? [];
}

export function nutrientsMatchPortion(baseNutrients: NutrientValue[], mealScale: number, totalNutrients: NutrientValue[]): boolean {
    const expected = applyNutrientPortion(baseNutrients, mealScale);
    const actual = normalizeNutrientValues(totalNutrients);
    if (expected.length !== actual.length) return false;
    return expected.every(row => {
        const match = actual.find(item => item.key === row.key);
        const tolerance = Math.max(0.05, Math.abs(row.amount) * 0.02);
        return match != null && Math.abs(match.amount - row.amount) <= tolerance;
    });
}

export type NutrientRow = {
    key: string;
    amount: number;
};

export function nutrientRowsFromValues(values: NutrientValue[] | null | undefined): NutrientRow[] {
    return normalizeNutrientValues(values).map(row => ({ key: row.key, amount: row.amount }));
}

export function nutrientValuesFromRows(rows: NutrientRow[] | null | undefined): NutrientValue[] {
    if (!rows?.length) return [];
    return normalizeNutrientValues(rows.flatMap(row => (isNutrientKey(row.key) ? [{ key: row.key, amount: row.amount }] : [])));
}
